import {FC, lazy, Suspense} from 'react';
import {IOnlineCondolenceDetails} from '../../types/resources/OnlineCondolenceDetails';
import Header from './Header';
import {DetailPageViewMode} from '../../enums/DetailPageViewMode';
import Tabs from '../Tabs';
import Tab from '../Tab';
import FuneralInfo from './FuneralInfo';
import LocationCollection from '../Location/LocationCollection';
import {useSettingsContext} from '../../context/settings/useSettingsContext';
import {useTranslation} from 'react-i18next';

const Gallery = lazy(() => import('../PhotoAlbum/Gallery'));
const CandleCollection = lazy(() => import('../Candle/CandleCollection'));
const PostsCollection = lazy(() => import('../Post/PostsCollection'));

type TProps = {
    onlineCondolence: IOnlineCondolenceDetails;
}

interface OnlineCondolenceProps {
    onlineCondolence: IOnlineCondolenceDetails;
}

const LazyLocationCollection = ({onlineCondolence}: OnlineCondolenceProps) => {
    return (
        <Suspense fallback={null}>
            <LocationCollection
                locations={onlineCondolence.locations}
            />
        </Suspense>
    );
}

const LazyGallery = ({onlineCondolence}: OnlineCondolenceProps) => {
    return (
        <Suspense fallback={null}>
            <Gallery items={onlineCondolence.photoAlbum} />
        </Suspense>
    );
}

const LazyCandleCollection = ({ onlineCondolence }: OnlineCondolenceProps) => {
    return (
        <Suspense fallback={null}>
            <CandleCollection
                onlineCondolence={onlineCondolence}
                isCandleLightingEnabled={onlineCondolence.enableCandleLightMessage}
                candles={onlineCondolence.candles}
            />
        </Suspense>
    );
}

const LazyPostsCollection = ({ onlineCondolence }: OnlineCondolenceProps) => {
    return (
        <Suspense fallback={null}>
            <PostsCollection
                onlineCondolence={onlineCondolence}
                initialPosts={onlineCondolence.posts}
            />
        </Suspense>
    );
}

const OnlineCondolenceDetail: FC<TProps> = ({onlineCondolence}) => {
    const {t} = useTranslation();
    const { detailPageViewMode } = useSettingsContext();

    return (
        <>
            <Header details={onlineCondolence}/>
            {detailPageViewMode === DetailPageViewMode.Tabs ? (
                <Tabs
                    id={'onlineCondolenceDetail'}
                    className={'cr-mt-lg cr-mb-xl'}
                    activeItemClasses={'cr-bg-secondary cr-border-secondary cr-text-white'}
                    itemClasses={'cr-bg-grey-light cr-border-grey-light cr-text-secondary'}
                    centerOnMobile={true}
                >
                    <Tab label={t('funeral-info')}>
                        <FuneralInfo onlineCondolence={onlineCondolence}/>
                        <LazyLocationCollection onlineCondolence={onlineCondolence} />
                    </Tab>
                    <Tab label={t('photos')}>
                        <LazyGallery onlineCondolence={onlineCondolence} />
                    </Tab>
                    <Tab label={t('messages-and-candles')}>
                        <LazyCandleCollection onlineCondolence={onlineCondolence}/>
                        <LazyPostsCollection onlineCondolence={onlineCondolence}/>
                    </Tab>
                </Tabs>
            ) : (
                <div>
                    <FuneralInfo onlineCondolence={onlineCondolence}/>
                    <LazyLocationCollection onlineCondolence={onlineCondolence} />
                    <LazyGallery onlineCondolence={onlineCondolence} />
                    <LazyCandleCollection onlineCondolence={onlineCondolence}/>
                    <LazyPostsCollection onlineCondolence={onlineCondolence}/>
                </div>
            )}
        </>
    );
}

export default OnlineCondolenceDetail;
